import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2, Minus, Plus, CreditCard, Loader2, ShoppingBag, Tag, FileText } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import { api, formatApiError } from "@/lib/api";

export function CartDrawer() {
  const { items, remove, setQty, total, open, setOpen } = useCart();
  const { t } = useLang();
  const [paying, setPaying] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [contactEmail, setContactEmail] = useState(() => localStorage.getItem("ergosafe_cart_email") || "");
  const nav = useNavigate();

  const discountedTotal = promo ? total * (1 - promo.percent / 100) : total;

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    try {
      const { data } = await api.post("/promos/validate", { code: promoInput });
      setPromo(data);
      toast.success(`Code ${data.code} appliqué : −${data.percent}%`);
    } catch (err) {
      setPromo(null);
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPromoLoading(false);
    }
  };

  const checkout = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout/cart", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.qty })),
        origin_url: window.location.origin,
        promo_code: promo?.code || null,
        contact_email: contactEmail.trim() || null,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPaying(false);
    }
  };

  const requestQuote = () => {
    const lines = items.map((i) => `- ${i.name} × ${i.qty} (${(i.price * i.qty).toFixed(2)} € HT)`).join("\n");
    setOpen(false);
    nav("/contact", {
      state: {
        subject: `Devis panier : ${items.length} article${items.length > 1 ? "s" : ""}`,
        message: `Bonjour, je souhaite un devis pour le panier suivant :\n${lines}\nTotal indicatif : ${total.toFixed(2)} € HT.\nMerci de me proposer vos conditions (volume, délais, livraison).`,
      },
    });
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60]" style={{ background: "rgba(15,17,21,0.5)" }}
            onClick={() => setOpen(false)} data-testid="cart-overlay" />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[70] w-full max-w-md flex flex-col"
            style={{ background: "var(--bone)", borderLeft: "1px solid var(--border)" }}
            data-testid="cart-drawer">
            <div className="flex items-center justify-between px-6 py-5" style={{ borderBottom: "1px solid var(--border)" }}>
              <h2 className="font-display font-black text-2xl">{t("Mon panier", "My cart")}</h2>
              <button onClick={() => setOpen(false)} className="p-2" data-testid="cart-close-btn"><X size={22} /></button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
                <ShoppingBag size={40} style={{ color: "var(--ink-2)" }} />
                <p style={{ color: "var(--ink-2)" }}>{t("Votre panier est vide.", "Your cart is empty.")}</p>
                <Link to="/catalogue" onClick={() => setOpen(false)} className="btn-primary px-6 py-3 font-semibold" data-testid="cart-empty-catalogue-link">
                  {t("Voir le catalogue", "View catalogue")}
                </Link>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-lenis-prevent>
                  {items.map((i) => (
                    <div key={i.id} className="flex gap-4 p-3" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid={`cart-item-${i.id}`}>
                      <img src={i.image} alt={i.name} className="w-16 h-16 object-cover shrink-0" style={{ border: "1px solid var(--border)" }} />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{i.name}</p>
                        <p className="text-sm mt-0.5" style={{ color: "var(--ink-2)" }}>{i.price.toFixed(2)} € HT</p>
                        <div className="flex items-center gap-2 mt-2">
                          <button onClick={() => setQty(i.id, i.qty - 1)} className="p-1" style={{ border: "1px solid var(--border)" }} data-testid={`cart-qty-minus-${i.id}`}><Minus size={13} /></button>
                          <span className="text-sm font-semibold w-6 text-center" data-testid={`cart-qty-${i.id}`}>{i.qty}</span>
                          <button onClick={() => setQty(i.id, i.qty + 1)} className="p-1" style={{ border: "1px solid var(--border)" }} data-testid={`cart-qty-plus-${i.id}`}><Plus size={13} /></button>
                        </div>
                      </div>
                      <div className="flex flex-col items-end justify-between">
                        <button onClick={() => remove(i.id)} data-testid={`cart-remove-${i.id}`}><Trash2 size={16} style={{ color: "var(--ink-2)" }} /></button>
                        <p className="font-display font-bold">{(i.price * i.qty).toFixed(2)} €</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-6 py-5 space-y-4" style={{ borderTop: "1px solid var(--border)", background: "var(--white)" }}>
                  <div className="flex gap-2" data-testid="promo-row">
                    <div className="relative flex-1">
                      <Tag size={15} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--ink-2)" }} />
                      <input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} placeholder="Code promo"
                        className="field" style={{ paddingLeft: "2.2rem" }} data-testid="promo-input" />
                    </div>
                    <button onClick={applyPromo} disabled={promoLoading} className="btn-outline px-4 py-2 text-sm font-semibold" data-testid="promo-apply-btn">
                      {promoLoading ? "…" : "Appliquer"}
                    </button>
                  </div>
                  {promo && (
                    <div className="flex items-center justify-between text-sm" data-testid="promo-applied">
                      <span className="flex items-center gap-2 font-semibold" style={{ color: "var(--orange)" }}>
                        <Tag size={14} /> {promo.code} (−{promo.percent}%)
                        <button onClick={() => { setPromo(null); setPromoInput(""); }} className="underline text-xs" style={{ color: "var(--ink-2)" }} data-testid="promo-remove-btn">retirer</button>
                      </span>
                      <span style={{ color: "var(--orange)" }}>−{(total - discountedTotal).toFixed(2)} €</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{t("Total HT", "Total excl. VAT")}</span>
                    <span className="font-display font-black text-2xl" data-testid="cart-total">
                      {promo && <span className="text-base font-normal line-through mr-2" style={{ color: "var(--ink-2)" }}>{total.toFixed(2)} €</span>}
                      {discountedTotal.toFixed(2)} €
                    </span>
                  </div>
                  <input type="email" value={contactEmail}
                    onChange={(e) => { setContactEmail(e.target.value); localStorage.setItem("ergosafe_cart_email", e.target.value); }}
                    placeholder="Votre email (rappel de panier, optionnel)" className="field" data-testid="cart-email-input" />
                  <p className="text-xs" style={{ color: "var(--ink-2)" }}>TVA calculée à l'étape de paiement · Paiement sécurisé Stripe</p>
                  <button onClick={checkout} disabled={paying} className="btn-primary w-full py-4 font-semibold flex items-center justify-center gap-2" data-testid="cart-checkout-btn">
                    {paying ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />}
                    {paying ? t("Redirection…", "Redirecting…") : t("Payer ma commande", "Pay now")}
                  </button>
                  <button onClick={requestQuote} className="btn-outline w-full py-3 font-semibold flex items-center justify-center gap-2" data-testid="cart-quote-btn">
                    <FileText size={16} /> {t("Demander un devis pour ce panier", "Request a quote for this cart")}
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
